import Node from '../ui/Node';
import mixin from '../privates/utils/mixin';
import ViewportMixin from './mixins/ViewportMixin';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import _ from 'lodash';

const SLIDE_TYPES = new Set(['wysiwyg.viewer.components.BoxSlideShowSlide', 
    'wysiwyg.viewer.components.StripContainerSlideShowSlide',
    'wysiwyg.viewer.components.StateBoxState',
    'wysiwyg.viewer.components.StateBoxFormState',
    'wysiwyg.viewer.components.StateStripState']);

/**
 * @mixin Element
 * @summary Provides basic functionality for elements.
 * @mixes $w.Node
 * @mixes $w.ViewportMixin
 * @memberof $w
 */
class Element extends Node {


    // PROPERTIES:

    /**
     * @member rendered
     * @label property
     * @syntax
     * get rendered(): boolean
     * @summary Indicates if an element is currently displayed.
     * @description
     *  If `rendered` is `true`, the element is in the current DOM structure and
     *  can be used.
     *
     *  If `rendered` is `false` the element is not in the current DOM structure.
     *
     *  An element might not be in the DOM if it is in a [slide]($w.Slide.html) or a [state]($w.State.html)
     *  which is not currently showing.
     * @snippet [Element-rendered.es6=Get an element's rendered status]
     * @type {external:Boolean}
     * @default false
     * @memberof $w.Element
     * @see [collapsed]($w.HiddenMixin.html#collapsed)
     * @see [hidden]($w.HiddenMixin.html#hidden)
     * @see [isVisible]($w.HiddenMixin.html#isVisible)
     * @readonly
     */
    get rendered() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        if (RMI.isGhostComponent(compId)) {
            return false;
        }

        let parentId = compId;
        while (parentId) {
            const parentType = _.get(RMI.getComp(parentId), 'type');
            if (SLIDE_TYPES.has(parentType)) {
                const slideId = parentId;
                const slideshowId = RMI.getParent(parentId);
                const currentSlideIndex = _.get(RMI.getState(slideshowId), 'currentIndex');
                const displayedSlideId = _.get(RMI.getChildren(slideshowId), currentSlideIndex);
                return slideId === displayedSlideId;
            }
            parentId = RMI.getParent(parentId);
        }
        return RMI.getComp(compId).isDisplayed;
    }

    // METHODS:

    /**
     * @function onMouseIn
     * @syntax
     * function onMouseIn(handler: MouseEventHandler): Element
     * callback MouseEventHandler(event: MouseEvent, $w: Function): void
     * @summary Adds an event handler that runs when the mouse pointer is moved
     *  onto the element.
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the mouse pointer is moved onto the
     *  element.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [Element-onMouseIn.es6=Get the mouse event info when the mouse enters an element]
     * @eventtype mouseenter
     * @memberof $w.Element
     * @eventhandler
     */
    onMouseIn(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onMouseIn',
            dataToValidate: {onMouseIn: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().MOUSE_IN, controllerId, cb);
        return this;
    }

    /**
     * @function onMouseOut
     * @syntax
     * function onMouseOut(handler: MouseEventHandler): Element
     * callback MouseEventHandler(event: MouseEvent, $w: Function): void
     * @summary Adds an event handler that runs when the mouse pointer is moved
     *  off of the element.
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.MouseEventHandler} handler The name of the function or
     *  the function expression to run when the mouse pointer is moved off of
     *  the element.
     * @returns {$w.Element} The element to which the event handler was added.
     * @snippet [Element-onMouseOut.es6=Get the mouse event info when the mouse exits an element]
     * @eventtype mouseleave
     * @memberof $w.Element
     * @eventhandler
     */
    onMouseOut(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onMouseOut',
            dataToValidate: {onMouseOut: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().MOUSE_OUT, controllerId, cb);
        return this;
    }

    toJSON() {
        const {id, type, global, rendered} = this;
        return {id, type, global, rendered};
    }
}

mixin(Element.prototype, ViewportMixin.prototype);

export default Element;
